import * as React from 'react';
import { PractiseRows, SkeletonRows, TextSkeleton, type CardProps, type FilterLink, type PractiseRow } from '@chess/api-insights/charts';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import { GROUND_CAUSE_VIEWS } from '@chess/api-mistakes';
import '@chess/api-ending-case/colors.css';
import { wentWrongOf, type LostGroundResult } from './LostGroundAggregator.ts';
import { LOST_GROUND_HELP } from './help.tsx';

// "Moves to practise" in the middlegame (2026-10-08): the period's
// middlegames that went wrong, one row per cause (Tactics, Missed chances,
// Time trouble, Positional: the "How you lost ground" causes, each game in
// the one that cost it the most), in games, each row the library filtered
// to them (`ground-cause`); each game's review has the move to practise as
// its puzzle. The same shape as the Ending section's card. The pie of the
// same numbers is "How you lost ground", in the Middlegame screen's "More".
// Drawn from props only: the card's body, which the section draws in the
// dashboard's frame (`PRACTISE_CARD`, `registerCard`).

export type PractiseCardProps = CardProps<{ result: LostGroundResult }, {
  /** Opens the library filtered to a row's games. */
  link?: FilterLink;
}>;

/** The frame's title and help topic: the registration's and the stories'. */
export const PRACTISE_CARD = { title: 'Moves to practise', help: LOST_GROUND_HELP };
const ASIDE = 'in the middlegame';
const LABEL: Record<string, string> = { tactic: 'Tactics', missed: 'Missed chances', time: 'Time trouble', positional: 'Positional' };
const middlegames = (n: number) => `${n} ${n === 1 ? 'middlegame' : 'middlegames'}`;

export const PractiseCard: React.FC<PractiseCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <SkeletonRows labels={GROUND_CAUSE_VIEWS.map((v) => LABEL[v.cause])} value="0" />
        <CardNote>
          <TextSkeleton sample="of 12 middlegames gone wrong · a row opens its games" />
        </CardNote>
      </>
    );
  const { result, link } = props;
  const total = wentWrongOf(result);
  const rows: PractiseRow[] = GROUND_CAUSE_VIEWS.map((v) => ({
    key: v.cause,
    label: LABEL[v.cause],
    count: result.byCause[v.cause],
    colour: v.colour,
    title: `${v.description} · ${middlegames(result.byCause[v.cause])} · opens them`,
    filter: { groundCause: v.cause },
  }));
  if (!total)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardEmpty>No middlegame went wrong in the last 30 days.</CardEmpty>
      </>
    );
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      <PractiseRows rows={rows} link={link} />
      <CardNote>{`of ${middlegames(total)} gone wrong · a row opens its games; each review has the puzzle`}</CardNote>
    </>
  );
};
